// The conversation under one In conversation row. Fetched on expand, never
// carried in the funnel payload — that stream is day-grain counts.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchActivityThread, panelMessage } from '../api/client';
import type { ActivityThreadMessage } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { SALESFORCE_ACCOUNT_URL } from '../lib/format';
import {
  channelLabel, directionLabel, formatThreadStamp, hasExpandableBody,
  threadHeadline, threadWho,
} from './thread';

function MessageBubble({ message }: { message: ActivityThreadMessage }) {
  const inbound = message.direction === 'received';
  const headline = threadHeadline(message);
  const [bodyOpen, setBodyOpen] = useState(false);
  const canExpand = hasExpandableBody(message.body, message.channel);
  return (
    <div className={'tmsg' + (inbound ? ' received' : ' sent')}>
      <div className="tbubble">
        <div className="thead">
          <span className="tdir">{directionLabel(message.direction, message.is_autoreply)}</span>
          <span className="tchan">{channelLabel(message.channel)}</span>
          <span className="twhen">{formatThreadStamp(message.occurred_at, message.day)}</span>
        </div>
        <p className={'tsubject' + (headline.isActivity ? ' activity' : '')}>
          {headline.isActivity ? <span className="tact">Activity · </span> : null}
          {headline.text}
        </p>
        <p className="twho">{threadWho(message)}</p>
        {message.call_outcome ? <p className="tmeta">{message.call_outcome}</p> : null}
        {canExpand ? (
          <>
            <button
              type="button"
              className="tbodybtn"
              aria-expanded={bodyOpen}
              onClick={() => setBodyOpen(open => !open)}
            >
              {bodyOpen ? 'Hide message' : 'Show message'}
            </button>
            {bodyOpen ? <div className="tbody">{message.body}</div> : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

export function AccountThread({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}) {
  const { getToken } = useAuthContext();
  const threadQuery = useQuery({
    queryKey: ['activity-thread', accountId],
    queryFn: () => fetchActivityThread(getToken, accountId),
  });

  if (threadQuery.isPending) {
    return <div className="thread"><p className="tnote">Reading the thread…</p></div>;
  }
  if (threadQuery.error) {
    return (
      <div className="thread">
        <p className="tnote err">
          {panelMessage(threadQuery.error, 'Could not load this thread.')}
        </p>
      </div>
    );
  }

  const payload = threadQuery.data;
  if (!payload) return null;

  return (
    <div className="thread">
      <div className="threadhead">
        <a
          href={`${SALESFORCE_ACCOUNT_URL}${accountId}/view`}
          target="_blank"
          rel="noopener noreferrer"
          className="msf"
        >
          Open {accountName} in Salesforce
        </a>
      </div>
      <p className="tnote">{payload.lag_note}</p>
      <p className="tnote">{payload.coverage_note}</p>
      {payload.truncated ? (
        <p className="tnote">Showing the {payload.messages.length} most recent logged touches.</p>
      ) : null}

      {payload.messages.length ? (
        <div className="tlist">
          {payload.messages.map((message, index) => (
            <MessageBubble
              key={`${message.occurred_at}-${message.direction}-${index}`}
              message={message}
            />
          ))}
        </div>
      ) : (
        <p className="tnote empty">
          {payload.found
            ? 'No activity for this account in the mirror. That can be a gap in the log, not that nothing happened.'
            : 'This account is not on a strategist’s book in the mirror.'}
        </p>
      )}
    </div>
  );
}
